/**
 * Zod schemas for the parsed `.canary.md` spec AST.
 *
 * The spec format is a single workflow per file. Frontmatter carries the strict
 * top-level fields; H2 sections carry prose (Spirit/Preflight/Cleanup) and
 * structured lists (Inputs, Scenarios, Attachments). The Journey body is a
 * linear sequence of bold lead-in nodes that map 1:1 to canary node types.
 *
 * @module spec/grammar
 */

import { InlineAssertionSchema } from '@chatsdet/types';
import { z } from 'zod';

/* ── Frontmatter ─────────────────────────────────────────────────────── */

/**
 * A reference to either a credential template (resolved at run/suite level)
 * or a specific raw credential by name. Used for both the frontmatter
 * `actor:` field and mid-journey `**Switch role to**` nodes.
 */
export const CredentialRefSchema = z.union([
  z.object({ kind: z.literal('template'), role: z.string().min(1) }),
  z.object({ kind: z.literal('credential'), name: z.string().min(1) }),
  /**
   * `seed-output <ref>` — the journey runs as a credential that was provisioned
   * by an upstream seed workflow. The `ref` is the seed's `dataKey`; the
   * runtime resolves it at suite-prep time via the seed_outputs lookup.
   */
  z.object({ kind: z.literal('seedOutput'), ref: z.string().min(1) }),
]);
export type CredentialRef = z.infer<typeof CredentialRefSchema>;

export type Actor = CredentialRef;

export const FrontmatterSchema = z
  .object({
    name: z.string().min(1),
    /**
     * Workflow ID this spec is bound to. Absent on first compile; the
     * compiler writes it back into the frontmatter after a successful
     * create. On recompile, the compiler keys the diff on this ID.
     */
    workflowId: z.string().uuid().optional(),
    status: z.enum(['draft', 'approved']).optional(),
    flowType: z.enum(['standard', 'setup', 'seed', 'teardown']).optional(),
    /**
     * Credential the journey runs as. Required for authenticated specs (the
     * common case); optional for unauthenticated flows, which have no leading
     * login node to represent.
     */
    actor: CredentialRefSchema.optional(),
    authenticated: z.boolean().default(true),
    tags: z.array(z.string()).optional(),
    requirementRef: z.string().optional(),
    apiOnly: z.boolean().optional(),
    sandboxTemplate: z.string().optional(),
    environments: z
      .union([
        z.object({ only: z.array(z.string()).min(1) }),
        z.object({ types: z.array(z.enum(['permanent', 'ephemeral', 'local', 'sandbox'])).min(1) }),
      ])
      .optional(),
    /**
     * Teardown linkage for `flowType: seed` workflows. Two surface forms:
     *   teardown: cleanup-demo-org              # shorthand, defaults applied
     *   teardown:
     *     spec: cleanup-demo-org                # nested for explicit overrides
     *     snapshotVersion: 3                    # or 'latest' (default)
     *     enabled: false                        # default true
     * The DB stores teardownWorkflowId/teardownSnapshotVersion/teardownEnabled
     * on the seed row; compile resolves spec name → id and PATCHes the seed,
     * pull reverses (FK → name).
     */
    teardown: z
      .union([
        z.string().min(1),
        z.object({
          spec: z.string().min(1),
          snapshotVersion: z
            .union([z.literal('latest'), z.number().int().nonnegative()])
            .optional(),
          enabled: z.boolean().optional(),
        }),
      ])
      .optional(),
  })
  .strict();
export type Frontmatter = z.infer<typeof FrontmatterSchema>;

/* ── Inputs / Scenarios / Attachments ────────────────────────────────── */

export const InputDeclSchema = z.object({
  name: z.string().min(1),
  kind: z.enum(['value', 'credential']).default('value'),
  defaultValue: z.string(),
});
export type InputDecl = z.infer<typeof InputDeclSchema>;

export const ScenarioSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  isDefault: z.boolean().default(false),
  includeInSuite: z.boolean().default(true),
  skipReason: z.string().optional(),
  values: z.record(z.string(), z.string()).default({}),
  credentials: z.record(z.string(), z.string()).default({}),
});
export type Scenario = z.infer<typeof ScenarioSchema>;

export const AttachmentDeclSchema = z.object({
  ref: z.string().min(1),
  source: z.string().min(1), // path or attachmentId
});
export type AttachmentDecl = z.infer<typeof AttachmentDeclSchema>;

/**
 * A seed workflow's declared output. Maps spec-author intent (dataKey + label +
 * description + property name) onto the DB row's authoritative
 * `seedOutputsJson[*]` shape. `propertyName` is resolved to `propertyId` at
 * compile by the CLI sync layer. Credential outputs (the richer
 * `credentialOutput` config) ride in a sidecar follow-up; for now the parser
 * surfaces a clear "this seed output has credentialOutput — author it in the
 * UI for now" issue rather than dropping the data.
 */
export const DeclaredOutputSchema = z.object({
  dataKey: z.string().min(1),
  label: z.string().min(1).optional(),
  description: z.string().optional(),
  propertyName: z.string().optional(),
});
export type DeclaredOutput = z.infer<typeof DeclaredOutputSchema>;

/* ── Journey nodes ───────────────────────────────────────────────────── */

const NodeBase = z.object({
  description: z.string().optional(),
  /**
   * Workflow node UUID — present after first compile, when the compiler
   * writes `<!-- @id <uuid> -->` next to the node's lead-in. Drives the
   * recompile diff.
   */
  nodeId: z.string().uuid().optional(),
  /**
   * Source line index of the lead-in. Used by the writeback step to
   * insert `<!-- @id <uuid> -->` after a CREATE without re-finding the
   * line. Populated by the parser; not user-authored.
   */
  sourceLine: z.number().int().nonnegative().optional(),
});

/**
 * Accept absolute http(s) URLs or root-relative paths only. Rejects
 * `javascript:`, `data:`, `file:`, custom schemes — important because the
 * spec is LLM-emitted and later loaded into a browser session.
 */
const PageUrlSchema = z
  .string()
  .min(1)
  .refine(
    (s) => {
      if (s.startsWith('/')) return true; // root-relative path
      try {
        const u = new URL(s);
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch {
        return false;
      }
    },
    {
      message:
        'pageUrl must be a root-relative path (e.g. /dashboard) or an absolute http(s) URL',
    }
  );

export const NavigateNodeSchema = NodeBase.extend({
  kind: z.literal('navigate'),
  pageUrl: PageUrlSchema,
  openInNewTab: z.boolean().optional(),
});

export const SwitchRoleNodeSchema = NodeBase.extend({
  kind: z.literal('switchRole'),
  credentialRef: CredentialRefSchema,
});

export const ActionFieldSchema = z.object({
  name: z.string().min(1),
  type: z.string().optional(),
  value: z.string(),
});

export const ActionNodeSchema = NodeBase.extend({
  kind: z.literal('action'),
  title: z.string().min(1),
  fields: z.array(ActionFieldSchema).default([]),
  capture: z.array(z.string()).default([]),
  assertions: z.array(InlineAssertionSchema).default([]),
  captureAudio: z.boolean().optional(),
  captureLabel: z.string().optional(),
  captureExtraMs: z.number().int().nonnegative().optional(),
  alwaysUseAgent: z.boolean().optional(),
});

export const AssertionNodeSchema = NodeBase.extend({
  kind: z.literal('assertion'),
  condition: z.string().min(1),
  severity: z.enum(['warn', 'fail', 'fatal']).default('fatal'),
  expectedOutcome: z.string().optional(),
  alwaysUseAgent: z.boolean().optional(),
});

export const SeedNodeSchema = NodeBase.extend({
  kind: z.literal('seed'),
  specName: z.string().min(1),
  version: z.union([z.number().int().nonnegative(), z.literal('latest')]).optional(),
  onFailure: z.enum(['block', 'warn']).optional(),
  mode: z.enum(['recommended', 'api', 'browser']).optional(),
  /** Per-request timeout (seconds) for the seed/teardown api_sequence calls (API mode only). */
  apiTimeoutSeconds: z.number().int().positive().optional(),
  scope: z.enum(['inline', 'suite']).optional(),
  runTeardown: z.boolean().optional(),
  /**
   * Per-node overrides for the seed flow's `kind: 'value'` workflow inputs,
   * keyed by input name. Only honored for inline-scoped seeds — suite-scoped
   * seeds run with the seed flow's input defaults. Spec syntax:
   * `- input.<name>: <value>` sub-bullets.
   */
  inputs: z.record(z.string(), z.string()).optional(),
});

export const SetupNodeSchema = NodeBase.extend({
  kind: z.literal('setup'),
  specName: z.string().min(1),
  version: z.union([z.number().int().nonnegative(), z.literal('latest')]).optional(),
  mode: z.enum(['inline', 'suite']).optional(),
});

export const WaitNodeSchema = NodeBase.extend({
  kind: z.literal('wait'),
  durationSeconds: z.number().int().positive(),
});

export const ConditionNodeSchema: z.ZodType<{
  kind: 'condition';
  expression: string;
  description?: string;
  nodeId?: string;
  sourceLine?: number;
  hints?: string;
  thenBranch: JourneyNode[];
  elseBranch: JourneyNode[];
}> = NodeBase.extend({
  kind: z.literal('condition'),
  expression: z.string().min(1),
  hints: z.string().optional(),
  thenBranch: z.lazy(() => z.array(JourneyNodeSchema)),
  elseBranch: z.lazy(() => z.array(JourneyNodeSchema)),
});

export const NotificationNodeSchema = NodeBase.extend({
  kind: z.literal('notification'),
  channel: z.string().min(1),
  to: z.string().min(1),
  subject: z.string().optional(),
  body: z.string().min(1),
  attach: z.string().optional(),
});

export const AudioPlayNodeSchema = NodeBase.extend({
  kind: z.literal('audio'),
  attachmentRef: z.string().min(1),
  loop: z.boolean().optional(),
  waitForCompletion: z.boolean().optional(),
  label: z.string().optional(),
});

export const AudioAssertionNodeSchema = NodeBase.extend({
  kind: z.literal('audioAssertion'),
  label: z.string().min(1),
  // Audio default stays 'fail' (not 'fatal' like standalone assertions):
  // audio assertions never halt mid-run, so fail ≈ fatal here and fail is the
  // historical default.
  severity: z.enum(['warn', 'fail', 'fatal']).default('fail'),
  prompt: z.string().optional(),
  model: z.enum(['gpt-audio', 'gemini-2.x']).optional(),
  minDurationMs: z.number().int().nonnegative().optional(),
  maxDurationMs: z.number().int().nonnegative().optional(),
  minRmsDb: z.number().optional(),
  silenceFractionMax: z.number().min(0).max(1).optional(),
  reference: z.string().optional(),
  envelopeCorrelationMin: z.number().min(-1).max(1).optional(),
});

export const ApiSequenceNodeSchema = NodeBase.extend({
  kind: z.literal('apiSequence'),
  title: z.string().min(1),
  apiEndpointContext: z.string().min(1),
  credentialInputName: z.string().optional(),
  /**
   * Optional sidecar YAML carrying the full ApiSequenceConfig (steps, exports,
   * validations). Path is resolved relative to the .canary.md file. When set,
   * compile POSTs the loaded config to `/api-sequences/save` after the node
   * sync; pull writes the file when an api_sequence row exists. Multiple
   * api_sequence nodes in the same workflow share one sidecar (the DB has a
   * unique index on workflow_id), so the path should typically be identical
   * across nodes — the parser warns when the paths conflict.
   */
  sidecar: z.string().min(1).optional(),
});

export const SandboxActionNodeSchema = NodeBase.extend({
  kind: z.literal('sandboxAction'),
  /** Slug of the sandbox action; resolved against the run env's template at run time. */
  actionSlug: z.string().min(1),
  /** Cosmetic display label. */
  label: z.string().optional(),
  /** Input params; values may carry `$var.*` / macro tokens (passed verbatim). */
  params: z.record(z.string(), z.string()).optional(),
  /**
   * Output variable keys the action produces (denormalized from its
   * outputSchema). Optional — compile resolves them from the action when
   * omitted and a verified template declares the slug.
   */
  outputs: z.array(z.string().min(1)).optional(),
});

export const McpNodeSchema = NodeBase.extend({
  kind: z.literal('mcp'),
  /** Lead-in title (`**MCP — <title>**`). */
  title: z.string().min(1),
  /** Plain-language goal the agent accomplishes via the MCP server's tools. */
  instructions: z.string().min(1),
  /**
   * MCP application NAME (an org property with `type: 'mcp'`). The spec
   * carries the name; the server's write chokepoint resolves it to the
   * `propertyId` the runtime reads (mirroring `credentialTemplateName`).
   */
  application: z.string().min(1),
  /** Persona (credential template) NAME supplying auth. Optional. */
  persona: z.string().optional(),
  /** Variable names the node must produce (→ config `declaredOutputs`). */
  outputs: z.array(z.string()).optional(),
  /** Tool names exposed to the agent (→ config `toolAllowlist`). */
  tools: z.array(z.string()).optional(),
});

export const EndNodeSchema = NodeBase.extend({
  kind: z.literal('end'),
  outcome: z.enum(['pass', 'fail']).default('pass'),
});

export type JourneyNode =
  | z.infer<typeof NavigateNodeSchema>
  | z.infer<typeof SwitchRoleNodeSchema>
  | z.infer<typeof ActionNodeSchema>
  | z.infer<typeof AssertionNodeSchema>
  | z.infer<typeof SeedNodeSchema>
  | z.infer<typeof SetupNodeSchema>
  | z.infer<typeof WaitNodeSchema>
  | { kind: 'condition'; expression: string; description?: string; nodeId?: string; sourceLine?: number; hints?: string; thenBranch: JourneyNode[]; elseBranch: JourneyNode[] }
  | z.infer<typeof NotificationNodeSchema>
  | z.infer<typeof AudioPlayNodeSchema>
  | z.infer<typeof AudioAssertionNodeSchema>
  | z.infer<typeof ApiSequenceNodeSchema>
  | z.infer<typeof SandboxActionNodeSchema>
  | z.infer<typeof McpNodeSchema>
  | z.infer<typeof EndNodeSchema>;

export const JourneyNodeSchema: z.ZodType<JourneyNode> = z.union([
  NavigateNodeSchema,
  SwitchRoleNodeSchema,
  ActionNodeSchema,
  AssertionNodeSchema,
  SeedNodeSchema,
  SetupNodeSchema,
  WaitNodeSchema,
  ConditionNodeSchema,
  NotificationNodeSchema,
  AudioPlayNodeSchema,
  AudioAssertionNodeSchema,
  ApiSequenceNodeSchema,
  SandboxActionNodeSchema,
  McpNodeSchema,
  EndNodeSchema,
]);

/* ── Whole-file AST ──────────────────────────────────────────────────── */

export const SpecAstSchema = z.object({
  frontmatter: FrontmatterSchema,
  spirit: z.string().optional(),
  preflight: z.string().optional(),
  cleanup: z.string().optional(),
  inputs: z.array(InputDeclSchema).default([]),
  scenarios: z.array(ScenarioSchema).default([]),
  attachments: z.array(AttachmentDeclSchema).default([]),
  declaredOutputs: z.array(DeclaredOutputSchema).default([]),
  journey: z.array(JourneyNodeSchema),
});
export type SpecAst = z.infer<typeof SpecAstSchema>;

/** Surface for lint/parse errors. */
export type SpecIssue = {
  severity: 'error' | 'warn';
  message: string;
  line?: number;
  hint?: string;
};
